// In-theme currency conversion - replaces the Nova/Booster app (window.baCurr).
//
// Converts every `.money`-classed element (Shopify's `money`/`money_with_currency` Liquid
// filters already wrap their output in this class) from the shop's real, VAT/duty-inclusive GBP
// presentment price into the customer's detected local currency, for DISPLAY ONLY - checkout
// always charges GBP (this store's sole settlement currency), this module never touches that.
// No VAT/duty recomputation happens here - that's already baked into the GBP figure by Liquid
// before it ever reaches the DOM (see snippets/product-price-block.liquid, cart-item.liquid).
//
// Rate source: Shopify's own public, unauthenticated, per-request `/services/javascripts/currencies.js`
// endpoint (base currency USD). Target currency is resolved from `customerCountryCode` (set in
// snippets/customjs.liquid from `localization.country.iso_code`, native Shopify Markets
// geo-detection/manual-override - no selector UI needed here) via `window.themeCountryCurrencyMap`
// (also rendered in customjs.liquid, from `localization.available_countries`).
//
// Rounding matches Nova's live config on this store (round_by_default: true,
// round_style: "removeDecimal"): every price, GBP included, is rounded to the nearest whole
// currency unit for display.
(function () {
  var RATES_ENDPOINT = '/services/javascripts/currencies.js';
  var RATES_CACHE_KEY = 'themeCurrencyRates';
  var RATES_CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12h, matches getEurToGbpRate's cache window in custom.js

  var ratesPromise = null;

  // Static ISO 3166-1 country -> ISO 4217 currency reference table, independent of how Shopify
  // Markets is configured. window.themeCountryCurrencyMap (built in snippets/customjs.liquid from
  // `country.currency.iso_code`) is NOT a country->currency reference table - it's the market's
  // configured PRESENTMENT currency, which on this single-settlement-currency (GBP) store is GBP
  // for every enabled country. Using that as the lookup meant every non-UK customer resolved to
  // 'GBP' and convertAll() silently no-opped for everyone, regardless of country - this table is
  // the actual fix. Kept deliberately broad (covers effectively every ISO country code) since any
  // country could be enabled in Shopify Markets later without needing a matching JS change.
  var COUNTRY_CURRENCY_MAP = {
    AD: 'EUR', AE: 'AED', AF: 'AFN', AG: 'XCD', AI: 'XCD', AL: 'ALL', AM: 'AMD', AO: 'AOA',
    AR: 'ARS', AS: 'USD', AT: 'EUR', AU: 'AUD', AW: 'AWG', AX: 'EUR', AZ: 'AZN', BA: 'BAM',
    BB: 'BBD', BD: 'BDT', BE: 'EUR', BF: 'XOF', BG: 'BGN', BH: 'BHD', BI: 'BIF', BJ: 'XOF',
    BL: 'EUR', BM: 'BMD', BN: 'BND', BO: 'BOB', BQ: 'USD', BR: 'BRL', BS: 'BSD', BT: 'BTN',
    BW: 'BWP', BY: 'BYN', BZ: 'BZD', CA: 'CAD', CC: 'AUD', CD: 'CDF', CF: 'XAF', CG: 'XAF',
    CH: 'CHF', CI: 'XOF', CK: 'NZD', CL: 'CLP', CM: 'XAF', CN: 'CNY', CO: 'COP', CR: 'CRC',
    CU: 'CUP', CV: 'CVE', CW: 'XCG', CX: 'AUD', CY: 'EUR', CZ: 'CZK', DE: 'EUR', DJ: 'DJF',
    DK: 'DKK', DM: 'XCD', DO: 'DOP', DZ: 'DZD', EC: 'USD', EE: 'EUR', EG: 'EGP', EH: 'MAD',
    ER: 'ERN', ES: 'EUR', ET: 'ETB', FI: 'EUR', FJ: 'FJD', FK: 'FKP', FM: 'USD', FO: 'DKK',
    FR: 'EUR', GA: 'XAF', GB: 'GBP', GD: 'XCD', GE: 'GEL', GF: 'EUR', GG: 'GGP', GH: 'GHS',
    GI: 'GIP', GL: 'DKK', GM: 'GMD', GN: 'GNF', GP: 'EUR', GQ: 'XAF', GR: 'EUR', GS: 'GBP',
    GT: 'GTQ', GU: 'USD', GW: 'XOF', GY: 'GYD', HK: 'HKD', HN: 'HNL', HR: 'EUR', HT: 'HTG',
    HU: 'HUF', ID: 'IDR', IE: 'EUR', IL: 'ILS', IM: 'IMP', IN: 'INR', IO: 'USD', IQ: 'IQD',
    IR: 'IRR', IS: 'ISK', IT: 'EUR', JE: 'JEP', JM: 'JMD', JO: 'JOD', JP: 'JPY', KE: 'KES',
    KG: 'KGS', KH: 'KHR', KI: 'KID', KM: 'KMF', KN: 'XCD', KR: 'KRW', KW: 'KWD', KY: 'KYD',
    KZ: 'KZT', LA: 'LAK', LB: 'LBP', LC: 'XCD', LI: 'CHF', LK: 'LKR', LR: 'LRD', LS: 'LSL',
    LT: 'EUR', LU: 'EUR', LV: 'EUR', LY: 'LYD', MA: 'MAD', MC: 'EUR', MD: 'MDL', ME: 'EUR',
    MF: 'EUR', MG: 'MGA', MH: 'USD', MK: 'MKD', ML: 'XOF', MM: 'MMK', MN: 'MNT', MO: 'MOP',
    MP: 'USD', MQ: 'EUR', MR: 'MRU', MS: 'XCD', MT: 'EUR', MU: 'MUR', MV: 'MVR', MW: 'MWK',
    MX: 'MXN', MY: 'MYR', MZ: 'MZN', NA: 'NAD', NC: 'XPF', NE: 'XOF', NF: 'AUD', NG: 'NGN',
    NI: 'NIO', NL: 'EUR', NO: 'NOK', NP: 'NPR', NR: 'AUD', NU: 'NZD', NZ: 'NZD', OM: 'OMR',
    PA: 'PAB', PE: 'PEN', PF: 'XPF', PG: 'PGK', PH: 'PHP', PK: 'PKR', PL: 'PLN', PM: 'EUR',
    PN: 'NZD', PR: 'USD', PS: 'ILS', PT: 'EUR', PW: 'USD', PY: 'PYG', QA: 'QAR', RE: 'EUR',
    RO: 'RON', RS: 'RSD', RU: 'RUB', RW: 'RWF', SA: 'SAR', SB: 'SBD', SC: 'SCR', SD: 'SDG',
    SE: 'SEK', SG: 'SGD', SH: 'SHP', SI: 'EUR', SJ: 'NOK', SK: 'EUR', SL: 'SLE', SM: 'EUR',
    SN: 'XOF', SO: 'SOS', SR: 'SRD', SS: 'SSP', ST: 'STN', SV: 'USD', SX: 'ANG', SY: 'SYP',
    SZ: 'SZL', TC: 'USD', TD: 'XAF', TG: 'XOF', TH: 'THB', TJ: 'TJS', TK: 'NZD', TL: 'USD',
    TM: 'TMT', TN: 'TND', TO: 'TOP', TR: 'TRY', TT: 'TTD', TV: 'TVD', TW: 'TWD', TZ: 'TZS',
    UA: 'UAH', UG: 'UGX', US: 'USD', UY: 'UYU', UZ: 'UZS', VA: 'EUR', VC: 'XCD', VE: 'VES',
    VG: 'USD', VI: 'USD', VN: 'VND', VU: 'VUV', WF: 'XPF', WS: 'WST', YE: 'YER', YT: 'EUR',
    ZA: 'ZAR', ZM: 'ZMW', ZW: 'ZWG',
  };

  function getCustomerCountryCode() {
    if (typeof customerCountryCode !== 'undefined' && customerCountryCode) return customerCountryCode;
    if (typeof window.customerCountryCode !== 'undefined' && window.customerCountryCode) return window.customerCountryCode;
    return null;
  }

  function getCustomerCurrency() {
    var countryCode = getCustomerCountryCode();
    if (!countryCode) return 'GBP';
    if (COUNTRY_CURRENCY_MAP[countryCode]) return COUNTRY_CURRENCY_MAP[countryCode];
    // Fallback for a country Shopify Markets has enabled that isn't in the table above.
    var map = window.themeCountryCurrencyMap || {};
    return map[countryCode] || 'GBP';
  }

  var displayLocaleCache = {};

  // Resolves the BCP-47 locale a customer's own country natively formats money in (e.g. 'pl-PL',
  // 'en-US', 'de-DE'), so Intl.NumberFormat below renders the way that country's shoppers are used
  // to seeing prices - e.g. Nova's config showed Polish customers "123 zl" and US customers "$123".
  // Previously this module passed `undefined` (the *browser's* language) into Intl.NumberFormat,
  // which only coincidentally matches the customer's country and, for currencies most locales don't
  // have symbol data for (e.g. PLN outside pl-PL), or ones that need disambiguating (USD outside
  // en-US shows "US$", to distinguish from AUD/CAD/etc "$"), produced things like "PLN 123" or
  // "US$123" for customers whose browser language didn't happen to match their own country.
  // `Intl.Locale('und-<country>').maximize()` performs the same CLDR "add likely subtags" lookup
  // Intl.Locale is defined to do (e.g. und-PL -> pl-Latn-PL), letting the whole COUNTRY_CURRENCY_MAP
  // above stay a plain country->currency table instead of also needing a hand-maintained
  // country/currency->locale table like Nova's approach used.
  function getDisplayLocale(countryCode) {
    if (!countryCode) return undefined;
    if (Object.prototype.hasOwnProperty.call(displayLocaleCache, countryCode)) {
      return displayLocaleCache[countryCode];
    }

    var locale;
    try {
      locale = new Intl.Locale('und-' + countryCode).maximize().language + '-' + countryCode;
    } catch (e) {
      locale = undefined; // Intl.Locale unsupported, or an unparsable country code - fall back to browser locale
    }

    displayLocaleCache[countryCode] = locale;
    return locale;
  }

  // Parses a rendered Shopify `.money` string (e.g. "£1,234.00" from the `money` filter, or
  // "£1,234.00 GBP" from `money_with_currency`, per-block settings.show_currency_code) into a
  // plain numeric amount (major currency units) plus whether a currency-code suffix/prefix was
  // present. Reuses the same last-separator-wins grouping/decimal normalization already proven
  // in applyDutyVatMultiplierToThreshold() in custom.js, for consistent behavior theme-wide.
  function parseGbpMoneyText(text) {
    var trimmed = (text || '').trim();
    var match = trimmed.match(/^(\D*)([\d.,]+)(\D*)$/);
    if (!match) return null;

    var prefix = match[1];
    var numberPart = match[2];
    var suffix = match[3];
    var showCurrencyCode = /[A-Z]{2,4}/.test(prefix) || /[A-Z]{2,4}/.test(suffix);

    var lastSeparatorIndex = Math.max(numberPart.lastIndexOf('.'), numberPart.lastIndexOf(','));
    var normalized = numberPart;
    if (lastSeparatorIndex !== -1) {
      var intPart = numberPart.slice(0, lastSeparatorIndex).replace(/[.,]/g, '');
      var decPart = numberPart.slice(lastSeparatorIndex + 1);
      normalized = intPart + '.' + decPart;
    }

    var amount = parseFloat(normalized);
    if (isNaN(amount)) return null;

    return { amount: amount, showCurrencyCode: showCurrencyCode };
  }

  // Extracts the rates object from currencies.js's response text. That endpoint returns a plain
  // JS snippet (`var Currency = {"rates": {...}, ...};`), not JSON, so this can't be
  // response.json()'d directly - find the `Currency = {...}` object via brace-matching (robust
  // to exact whitespace/trailing-content variations) and JSON.parse just that slice.
  function extractRatesFromScript(scriptText) {
    var startMarker = 'Currency =';
    var startIdx = scriptText.indexOf(startMarker);
    if (startIdx === -1) return null;

    var openIdx = scriptText.indexOf('{', startIdx);
    if (openIdx === -1) return null;

    var depth = 0;
    var endIdx = -1;
    for (var i = openIdx; i < scriptText.length; i++) {
      var ch = scriptText[i];
      if (ch === '{') depth++;
      else if (ch === '}') {
        depth--;
        if (depth === 0) {
          endIdx = i;
          break;
        }
      }
    }
    if (endIdx === -1) return null;

    var parsed;
    try {
      parsed = JSON.parse(scriptText.slice(openIdx, endIdx + 1));
    } catch (e) {
      return null;
    }

    return (parsed && parsed.rates) ? parsed.rates : parsed;
  }

  function readCachedRates() {
    try {
      var cached = JSON.parse(localStorage.getItem(RATES_CACHE_KEY));
      if (cached && cached.rates && typeof cached.rates.GBP === 'number' && Date.now() - cached.ts < RATES_CACHE_TTL_MS) {
        return cached.rates;
      }
    } catch (e) {}
    return null;
  }

  function writeCachedRates(rates) {
    try {
      localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({ rates: rates, ts: Date.now() }));
    } catch (e) {}
  }

  function fetchLiveRates() {
    return fetch(RATES_ENDPOINT, { credentials: 'omit' })
      .then(function (response) {
        if (!response.ok) throw new Error('currencies.js response not ok');
        return response.text();
      })
      .then(function (text) {
        var rates = extractRatesFromScript(text);
        if (!rates || typeof rates.GBP !== 'number') throw new Error('missing GBP rate in currencies.js');
        return rates;
      });
  }

  // Static, checked-in snapshot (assets/currency-fallback-rates.json), used only if both the
  // live endpoint and the localStorage cache are unavailable. Its URL is rendered by Liquid into
  // window.themeCurrencyFallbackRatesUrl (snippets/customjs.liquid), same pattern as
  // window.themeEuFallbackRate in custom.js. Needs periodic manual refreshing since it's static.
  function fetchFallbackRates() {
    var url = window.themeCurrencyFallbackRatesUrl;
    if (!url) return Promise.resolve({ GBP: 1 });

    return fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('fallback rates asset not ok');
        return response.json();
      })
      .catch(function () {
        // Total failure: only GBP resolvable, so every non-GBP conversion below becomes a no-op
        // and elements simply keep showing their original GBP text - no crash, no blank prices.
        return { GBP: 1 };
      });
  }

  function getCurrencyRates() {
    if (ratesPromise) return ratesPromise;

    var cached = readCachedRates();
    if (cached) {
      ratesPromise = Promise.resolve(cached);
      return ratesPromise;
    }

    ratesPromise = fetchLiveRates()
      .then(function (rates) {
        writeCachedRates(rates);
        return rates;
      })
      .catch(function () {
        return fetchFallbackRates();
      });

    return ratesPromise;
  }

  function convertMoneyElement(el, rates, targetCurrency, displayLocale) {
    var rate = rates[targetCurrency];
    if (typeof rate !== 'number' || typeof rates.GBP !== 'number') return;

    var sourceText = el.dataset.gbpOriginal !== undefined ? el.dataset.gbpOriginal : el.textContent;
    if (el.dataset.gbpOriginal === undefined) el.dataset.gbpOriginal = sourceText;

    var parsed = parseGbpMoneyText(sourceText);
    if (!parsed) return;

    var convertedAmount = Math.round(parsed.amount * (rate / rates.GBP));

    var formatted;
    try {
      formatted = new Intl.NumberFormat(displayLocale, {
        style: 'currency',
        currency: targetCurrency,
        currencyDisplay: parsed.showCurrencyCode ? 'code' : 'symbol',
        // Forces plain 0-9 digits even in locales whose native numbering system isn't Latin
        // (Arabic-Indic, Devanagari, etc.) - the rest of this English-language storefront is Latin
        // digits throughout, so only the symbol/grouping/placement should follow the local convention.
        numberingSystem: 'latn',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(convertedAmount);
    } catch (e) {
      return; // unrecognized currency code - leave the original GBP text untouched
    }

    // theme amendment coordination - .free-shipping-threshold.money elements get a duty/VAT
    // multiplier applied on top by applyDutyVatMultiplierToThreshold() in custom.js, guarded by
    // data-duty-vat-applied so it only fires once per element. That guard is intentionally left
    // untouched here: el.dataset.gbpOriginal above is captured once and reused forever, so the
    // multiplier only ever needs to run once, against the raw GBP figure, before the very first
    // conversion - clearing the guard on every reconversion previously caused the announcement
    // bar's mutation observer to re-run the multiplier against the already-converted (non-GBP)
    // text on every refresh, compounding it (e.g. a 20%-VAT threshold drifting to +44% instead
    // of +20% after a couple of reconversions).
    //
    // theme amendment - skip the write entirely if the text wouldn't actually change. convertAll()
    // re-runs often (cart/quickview/variant events, debounced refreshes, etc.) and previously wrote
    // el.textContent unconditionally every time, even when `formatted` was identical to what's
    // already there. Each write is itself a DOM mutation, which any MutationObserver watching this
    // subtree (e.g. the cart drawer's) sees as new activity - potentially cascading into further
    // reactive refreshes. Elements no other script touches would settle after one real write either
    // way, but this closes off the possibility of a converted element ever being a source of
    // "content keeps changing" feedback, which is cheap insurance and costs nothing when the app
    // that also touches .money elements (Nova/Booster, see changelog) is still active.
    if (el.textContent === formatted) return;

    el.textContent = formatted;
    el.classList.add('theme-currency-converted');
  }

  function convertAll() {
    var targetCurrency = getCustomerCurrency();
    var displayLocale = getDisplayLocale(getCustomerCountryCode());

    // theme amendment - GBP customers still get their prices rounded to the nearest whole pound
    // (matching every other currency's rounding), just not converted. Feeding convertMoneyElement()
    // a synthetic { GBP: 1 } rate table with targetCurrency 'GBP' makes its rate/rates.GBP ratio
    // exactly 1, so it rounds without changing currency - no live rates fetch needed for this case.
    if (targetCurrency === 'GBP') {
      document.querySelectorAll('.money').forEach(function (el) {
        convertMoneyElement(el, { GBP: 1 }, 'GBP', displayLocale);
      });
      return;
    }
    // end theme amendment

    getCurrencyRates().then(function (rates) {
      if (!rates || typeof rates.GBP !== 'number') return;
      document.querySelectorAll('.money').forEach(function (el) {
        convertMoneyElement(el, rates, targetCurrency, displayLocale);
      });
    });
  }

  // Call this after directly overwriting a persistent .money element's textContent with a fresh
  // GBP figure from outside this module (e.g. writeEuConvertedRow() in custom.js, which reuses
  // the same DOM node across cart updates instead of re-rendering it) - otherwise convertAll()
  // would keep reconverting from a stale stashed original instead of the just-written new GBP text.
  function markFreshGbp(el) {
    if (el && el.dataset && el.dataset.gbpOriginal !== undefined) {
      delete el.dataset.gbpOriginal;
    }
  }

  window.themeCurrencyConverter = {
    convertAll: convertAll,
    markFreshGbp: markFreshGbp,
  };
})();
